import * as THREE from 'three';
import { fbm, worley, clamp01, smoothstep } from '../core/Noise.js';
import { decalVert, decalFrag } from './shaders/decal.glsl.js';

/**
 * Decal pool.
 *
 * Bullet holes are the persistent record of a firefight — the thing the player
 * looks at *after* the shooting stops — so they get a real crater normal map, a
 * dark rim, radial cracking and material-specific art rather than one grey dot
 * reused everywhere.
 *
 * Implementation notes:
 *  - One InstancedMesh, one draw call, fixed capacity from `ctx.quality.maxDecals`.
 *  - Instances are write-once; growth and fade are evaluated in the shader from
 *    `uTime`, so a wall covered in holes costs nothing per frame.
 *  - Recycling is oldest-first, and the pool *pre-schedules* the fade on the slot
 *    it is about to reuse (see RECYCLE_LEAD) so a decal is already invisible by the
 *    time its slot comes round. Decals never blink out.
 */

export const TUNING = {
  holeLife: 90,
  holeFade: 4,
  bloodLife: 75,
  bloodFade: 8,
  poolLife: 120,
  poolFade: 10,
  poolGrow: 3.5,
  scorchLife: 120,
  scorchFade: 8,
  recycleLead: 10, // slots of warning before a slot is reused
  recycleFade: 0.5,
  normalOffset: 0.008,
  atlasCell: 192,
};

export const DECAL = {
  HOLE_CONCRETE: 0,
  HOLE_METAL: 1,
  HOLE_WOOD: 2,
  GLASS_CRACK: 3,
  BLOOD_SPLAT: 4,
  BLOOD_POOL: 5,
  SCORCH: 6,
  RIPPLE: 7,
};

const COLS = 4;
const ROWS = 2;

/* ========================================================================== *
 * Procedural decal atlas
 * ========================================================================== */

/** Angular distance between two headings, in [-PI, PI]. */
function angDelta(a, b) {
  let d = a - b;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return d;
}

/**
 * Radial fracture pattern. Real impact cracks fan out from the hole, taper, and
 * are never evenly spaced — so the angles are jittered and each crack gets its own
 * length and width.
 */
function cracks(ang, r, count, seed, spread, maxLen) {
  let out = 0;
  for (let k = 0; k < count; k++) {
    const s = Math.sin((k + 1) * 12.9898 + seed) * 43758.5453;
    const j = s - Math.floor(s);
    const s2 = Math.sin((k + 1) * 78.233 + seed) * 12345.6789;
    const j2 = s2 - Math.floor(s2);
    const a0 = ((k + j * 0.75) / count) * Math.PI * 2;
    const len = maxLen * (0.35 + j2 * 0.65);
    if (r > len) continue;
    const d = Math.abs(angDelta(ang, a0));
    const w = spread * (0.25 + r * 1.6);
    out = Math.max(out, (1 - smoothstep(0, w, d)) * (1 - smoothstep(len * 0.5, len, r)));
  }
  return out;
}

export function buildDecalAtlasData(cell) {
  const W = COLS * cell;
  const H = ROWS * cell;
  const albedo = new Uint8ClampedArray(W * H * 4);
  const height = new Float32Array(W * H);
  const s = { r: 0, g: 0, b: 0, a: 0, h: 0.5 };

  const samplers = [
    /* ---------- 0: concrete bullet hole ---------- */
    (u, v, o) => {
      const dx = u - 0.5;
      const dy = v - 0.5;
      const r = Math.hypot(dx, dy) * 2;
      const ang = Math.atan2(dy, dx);
      const jag = fbm(Math.cos(ang) * 4 + 7, Math.sin(ang) * 4 + 7, 8, 3, 91);
      const rr = r + (jag - 0.5) * 0.16;

      const core = 1 - smoothstep(0.15, 0.30, rr);
      const rim = smoothstep(0.18, 0.32, rr) * (1 - smoothstep(0.32, 0.55, rr));
      const speckle = fbm(u * 26, v * 26, 26, 3, 17);
      const dust = (1 - smoothstep(0.36, 0.95, rr)) * clamp01(speckle * 1.5 - 0.4);
      // Short hairline fractures. Long spikes read as a cartoon starburst.
      const crk = cracks(ang, rr, 9, 3.1, 0.030, 0.62) * (1 - core);

      // Pulverised concrete is much lighter than the wall it came out of.
      const rimC = 0.60 + speckle * 0.20;
      o.r = 0.035 * (1 - core * 0.6) + rimC * rim + 0.55 * dust;
      o.g = 0.033 * (1 - core * 0.6) + rimC * 0.99 * rim + 0.53 * dust;
      o.b = 0.031 * (1 - core * 0.6) + rimC * 0.95 * rim + 0.50 * dust;
      o.r *= 1 - crk * 0.8; o.g *= 1 - crk * 0.8; o.b *= 1 - crk * 0.8;
      o.a = clamp01(core + rim * 0.95 + dust * 0.55 + crk * 0.8);
      o.h = 0.55 - core * 0.55 + rim * 0.22 - crk * 0.18;
    },

    /* ---------- 1: metal bullet hole ---------- */
    (u, v, o) => {
      const dx = u - 0.5;
      const dy = v - 0.5;
      const r = Math.hypot(dx, dy) * 2;
      const ang = Math.atan2(dy, dx);
      const petal = 0.5 + Math.sin(ang * 6 + fbm(Math.cos(ang) * 3, Math.sin(ang) * 3, 6, 2, 55) * 6) * 0.5;
      const rr = r - petal * 0.06;

      const core = 1 - smoothstep(0.10, 0.20, rr);
      // Torn metal folds outward and the bare edge is bright and unrusted.
      const lip = smoothstep(0.12, 0.20, rr) * (1 - smoothstep(0.20, 0.34, rr));
      // Metal does not pulverise, so the halo is a thin ring of bare scratched
      // steel rather than the dust cloud a masonry hole gets.
      const scuff = (1 - smoothstep(0.22, 0.42, rr)) * clamp01(fbm(u * 40, v * 40, 40, 2, 71) * 1.5 - 0.6);

      const bright = 0.62 + petal * 0.3;
      o.r = 0.02 * core + bright * lip + 0.26 * scuff;
      o.g = 0.02 * core + bright * lip + 0.26 * scuff;
      o.b = 0.02 * core + bright * 1.04 * lip + 0.27 * scuff;
      o.a = clamp01(core + lip * 1.0 + scuff * 0.3);
      o.h = 0.5 - core * 0.5 + lip * 0.42;
    },

    /* ---------- 2: wood bullet hole ---------- */
    (u, v, o) => {
      const dx = u - 0.5;
      const dy = v - 0.5;
      const r = Math.hypot(dx, dy) * 2;
      const ang = Math.atan2(dy, dx);
      // Splinters run long and thin, biased along the grain (here, the U axis).
      const grainBias = 1 - Math.abs(Math.sin(ang)) * 0.55;
      const spl = cracks(ang, r, 11, 8.7, 0.040, 0.62 * grainBias);
      const jag = fbm(Math.cos(ang) * 5 + 2, Math.sin(ang) * 5 + 2, 8, 3, 133);
      const rr = r + (jag - 0.5) * 0.2;
      const core = 1 - smoothstep(0.13, 0.27, rr);
      const rim = smoothstep(0.16, 0.29, rr) * (1 - smoothstep(0.29, 0.46, rr));

      // Freshly exposed timber is pale and only slightly warm — not orange.
      const pale = 0.34 + fbm(u * 30, v * 12, 30, 2, 151) * 0.16;
      o.r = 0.05 * core + pale * 1.00 * (rim + spl * 0.75);
      o.g = 0.035 * core + pale * 0.82 * (rim + spl * 0.75);
      o.b = 0.025 * core + pale * 0.58 * (rim + spl * 0.75);
      o.a = clamp01(core + rim * 0.9 + spl * 0.85);
      o.h = 0.55 - core * 0.5 + (rim + spl) * 0.25;
    },

    /* ---------- 3: glass crack ---------- */
    (u, v, o) => {
      const dx = u - 0.5;
      const dy = v - 0.5;
      const r = Math.hypot(dx, dy) * 2;
      const ang = Math.atan2(dy, dx);
      const radial = cracks(ang, r, 13, 21.3, 0.03, 1.0);
      // Concentric fractures link the radials — that lattice is what says "glass".
      const w = worley(u * 7, v * 7, 7, 313);
      const web = (1 - smoothstep(0.0, 0.035, w.f2 - w.f1)) * (1 - smoothstep(0.2, 0.95, r));
      const core = 1 - smoothstep(0.03, 0.13, r);
      const frost = (1 - smoothstep(0.05, 0.4, r)) * clamp01(fbm(u * 50, v * 50, 50, 2, 331) * 1.6 - 0.6);

      const c = clamp01(radial * 0.95 + web * 0.8 + core + frost * 0.7);
      o.r = 0.86; o.g = 0.92; o.b = 1.0;
      o.a = c * 0.92;
      o.h = 0.5 + c * 0.35 - core * 0.4;
    },

    /* ---------- 4: blood spatter ---------- */
    (u, v, o) => {
      const dx = u - 0.5;
      const dy = v - 0.5;
      const r = Math.hypot(dx, dy) * 2;
      const wob = fbm(u * 4, v * 4, 4, 4, 401);
      // Main body, plus droplets thrown along +V so the sprite has a direction.
      let a = 1 - smoothstep(0.22, 0.5, r + (wob - 0.5) * 0.5);
      const cellN = worley(u * 9, v * 6 + 1.5, 9, 419);
      const drops = (1 - smoothstep(0.015, 0.075, cellN.f1)) * (1 - smoothstep(0.3, 1.05, r)) * clamp01(0.35 + v);
      a = clamp01(Math.max(a, drops));
      const streak = clamp01(1 - smoothstep(0.03, 0.14, Math.abs(dx))) * (1 - smoothstep(0.2, 0.85, Math.abs(dy) * 2)) * (v > 0.5 ? 0.7 : 0);
      a = clamp01(Math.max(a, streak * 0.8));

      // Fresh blood is very dark and slightly desaturated at the thin edges.
      const thick = smoothstep(0.1, 0.7, a);
      o.r = 0.10 + thick * 0.18;
      o.g = 0.008 + thick * 0.012;
      o.b = 0.010 + thick * 0.010;
      o.a = a;
      o.h = 0.5 + a * 0.16;
    },

    /* ---------- 5: blood pool ---------- */
    (u, v, o) => {
      const dx = u - 0.5;
      const dy = v - 0.5;
      const r = Math.hypot(dx, dy) * 2;
      const wob = fbm(u * 3, v * 3, 3, 4, 503) + fbm(u * 9, v * 9, 9, 3, 509) * 0.3;
      const a = clamp01(1 - smoothstep(0.55, 0.92, r + (wob - 0.65) * 0.55));
      // Serum separates at the edge of a pool and reads lighter and browner.
      const edge = clamp01(1 - Math.abs(a - 0.35) * 5) * 0.8;
      o.r = 0.075 + a * 0.10 + edge * 0.09;
      o.g = 0.006 + a * 0.006 + edge * 0.028;
      o.b = 0.008 + a * 0.005 + edge * 0.012;
      o.a = clamp01(a * 1.15);
      o.h = 0.5 + a * 0.1;
    },

    /* ---------- 6: scorch ---------- */
    (u, v, o) => {
      const dx = u - 0.5;
      const dy = v - 0.5;
      const r = Math.hypot(dx, dy) * 2;
      const ang = Math.atan2(dy, dx);
      const flick = fbm(Math.cos(ang) * 3 + 11, Math.sin(ang) * 3 + 11, 6, 4, 601);
      const soot = fbm(u * 12, v * 12, 12, 4, 617);
      const streaks = clamp01(cracks(ang, r, 17, 44.1, 0.13, 1.0) * 0.6);
      const a = clamp01((1 - smoothstep(0.15, 0.85 + flick * 0.25, r)) * (0.55 + soot * 0.7) + streaks * 0.35 * (1 - smoothstep(0.5, 1.0, r)));
      const c = 0.018 + soot * 0.03;
      o.r = c * 1.1; o.g = c; o.b = c * 0.95;
      o.a = a;
      o.h = 0.5 - a * 0.05;
    },

    /* ---------- 7: ripple / wet ring ---------- */
    (u, v, o) => {
      const dx = u - 0.5;
      const dy = v - 0.5;
      const r = Math.hypot(dx, dy) * 2;
      const wob = fbm(u * 6, v * 6, 6, 3, 701);
      const rr = r + (wob - 0.5) * 0.12;
      const ring1 = clamp01(1 - Math.abs(rr - 0.78) * 11);
      const ring2 = clamp01(1 - Math.abs(rr - 0.52) * 15) * 0.55;
      const ring3 = clamp01(1 - Math.abs(rr - 0.30) * 20) * 0.35;
      // Barely any fill: a ripple is the rings. Filling the disc turns it into a
      // solid plate stuck to the surface.
      const wet = (1 - smoothstep(0.2, 0.95, rr)) * 0.07;
      o.r = 0.55; o.g = 0.62; o.b = 0.68;
      o.a = clamp01(ring1 + ring2 + ring3 + wet);
      o.h = 0.5 + (ring1 - ring2) * 0.3;
    },
  ];

  for (let ci = 0; ci < samplers.length; ci++) {
    const ox = (ci % COLS) * cell;
    const oy = Math.floor(ci / COLS) * cell;
    const fn = samplers[ci];
    for (let y = 0; y < cell; y++) {
      for (let x = 0; x < cell; x++) {
        s.r = s.g = s.b = 0; s.a = 0; s.h = 0.5;
        fn((x + 0.5) / cell, (y + 0.5) / cell, s);
        const px = ox + x;
        const py = oy + y;
        const o = (py * W + px) * 4;
        albedo[o] = clamp01(s.r) * 255;
        albedo[o + 1] = clamp01(s.g) * 255;
        albedo[o + 2] = clamp01(s.b) * 255;
        albedo[o + 3] = clamp01(s.a) * 255;
        height[py * W + px] = s.h;
      }
    }
  }

  // Sobel the height field into a tangent-space normal map. Sampling is clamped
  // per-cell so one decal's crater never bleeds a seam into its neighbour.
  const normal = new Uint8ClampedArray(W * H * 4);
  const at = (x, y, ox, oy) => {
    const cx = ox + Math.min(cell - 1, Math.max(0, x));
    const cy = oy + Math.min(cell - 1, Math.max(0, y));
    return height[cy * W + cx];
  };
  for (let ci = 0; ci < samplers.length; ci++) {
    const ox = (ci % COLS) * cell;
    const oy = Math.floor(ci / COLS) * cell;
    for (let y = 0; y < cell; y++) {
      for (let x = 0; x < cell; x++) {
        const gx =
          at(x - 1, y - 1, ox, oy) + 2 * at(x - 1, y, ox, oy) + at(x - 1, y + 1, ox, oy) -
          (at(x + 1, y - 1, ox, oy) + 2 * at(x + 1, y, ox, oy) + at(x + 1, y + 1, ox, oy));
        const gy =
          at(x - 1, y - 1, ox, oy) + 2 * at(x, y - 1, ox, oy) + at(x + 1, y - 1, ox, oy) -
          (at(x - 1, y + 1, ox, oy) + 2 * at(x, y + 1, ox, oy) + at(x + 1, y + 1, ox, oy));
        let nx = gx * 3.4;
        let ny = gy * 3.4;
        const nz = 1;
        const inv = 1 / Math.hypot(nx, ny, nz);
        nx *= inv; ny *= inv;
        const o = ((oy + y) * W + ox + x) * 4;
        normal[o] = (nx * 0.5 + 0.5) * 255;
        normal[o + 1] = (ny * 0.5 + 0.5) * 255;
        normal[o + 2] = nz * inv * 255;
        normal[o + 3] = 255;
      }
    }
  }

  return { albedo, normal, width: W, height: H };
}

function makeDecalAtlas(cell) {
  const { albedo, normal, width: W, height: H } = buildDecalAtlasData(cell);
  const mk = (data, srgb) => {
    const t = new THREE.DataTexture(data, W, H, THREE.RGBAFormat);
    t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
    t.magFilter = THREE.LinearFilter;
    t.minFilter = THREE.LinearMipmapLinearFilter;
    t.generateMipmaps = true;
    t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
    t.anisotropy = 4;
    t.needsUpdate = true;
    return t;
  };

  return { albedo: mk(albedo, true), normal: mk(normal, false) };
}

/* ========================================================================== *
 * Pool
 * ========================================================================== */

const STRIDES = { dPos: 3, dQuat: 4, dParams: 4, dCol: 4, dExtra: 4 };
const _Z = new THREE.Vector3(0, 0, 1);

export class Decals {
  constructor(ctx, root) {
    this.ctx = ctx;
    this.time = 0;
    this.capacity = Math.max(32, ctx.quality.maxDecals ?? 128);
    this.head = 0;

    const cell = ctx.quality.level === 'medium' ? 128 : TUNING.atlasCell;
    const tex = makeDecalAtlas(cell);
    this.textures = tex;

    this.uniforms = {
      uTime: { value: 0 },
      uAlbedo: { value: tex.albedo },
      uNormal: { value: tex.normal },
      uAtlasGrid: { value: new THREE.Vector2(COLS, ROWS) },
      uSunDir: { value: new THREE.Vector3(0.5, 0.8, 0.35).normalize() },
      uSunColor: { value: new THREE.Color(1.5, 1.35, 1.12) },
      uAmbient: { value: new THREE.Color(0.34, 0.39, 0.48) },
      uCamPos: { value: new THREE.Vector3() },
    };

    this.material = new THREE.ShaderMaterial({
      vertexShader: decalVert,
      fragmentShader: decalFrag,
      uniforms: this.uniforms,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: THREE.CustomBlending,
      blendSrc: THREE.OneFactor,
      blendDst: THREE.OneMinusSrcAlphaFactor,
      // Both a geometric offset along the normal *and* a depth-slope offset: the
      // first handles grazing angles, the second handles head-on z-fighting.
      polygonOffset: true,
      polygonOffsetFactor: -6,
      polygonOffsetUnits: -12,
      side: THREE.DoubleSide,
    });

    const geo = new THREE.InstancedBufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute([-0.5, -0.5, 0, 0.5, -0.5, 0, 0.5, 0.5, 0, -0.5, 0.5, 0], 3));
    geo.setAttribute('uv', new THREE.Float32BufferAttribute([0, 0, 1, 0, 1, 1, 0, 1], 2));
    geo.setIndex([0, 1, 2, 0, 2, 3]);
    geo.instanceCount = 0;
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);

    this.arrays = {};
    this.attrs = {};
    for (const name in STRIDES) {
      const arr = new Float32Array(this.capacity * STRIDES[name]);
      const attr = new THREE.InstancedBufferAttribute(arr, STRIDES[name]);
      attr.setUsage(THREE.DynamicDrawUsage);
      geo.setAttribute(name, attr);
      this.arrays[name] = arr;
      this.attrs[name] = attr;
    }

    this.geometry = geo;
    this.mesh = new THREE.Mesh(geo, this.material);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 5;
    this.mesh.matrixAutoUpdate = false;
    root.add(this.mesh);

    this._q = new THREE.Quaternion();
    this._q2 = new THREE.Quaternion();
    this._n = new THREE.Vector3();
    this._lo = -1;
    this._hi = -1;
    this._wrapped = false;
  }

  _touch(i) {
    if (this._lo < 0 || i < this._lo) this._lo = i;
    if (i > this._hi) this._hi = i;
  }

  /**
   * Place a decal. All arguments are primitives so a burst of impacts allocates
   * nothing.
   *
   * @param {number} cellIndex one of DECAL.*
   * @param {number} size      quad edge length in metres
   * @param {number} gloss     0 = matte, 1 = wet
   */
  spawn(px, py, pz, nx, ny, nz, cellIndex, size, r, g, b, life, fadeDur, growTime = 0, growFrom = 1, gloss = 0) {
    const i = this.head;
    this.head++;
    if (this.head >= this.capacity) {
      this.head = 0;
      this._wrapped = true;
    }

    // Warn the slot we will reach in RECYCLE_LEAD spawns that its time is up, so
    // it fades out gracefully long before it is overwritten.
    this._retire((i + TUNING.recycleLead) % this.capacity);

    this._n.set(nx, ny, nz).normalize();
    this._q.setFromUnitVectors(_Z, this._n);
    this._q2.setFromAxisAngle(this._n, Math.random() * Math.PI * 2);
    this._q.premultiply(this._q2);

    const A = this.arrays;
    let o = i * 3;
    const off = TUNING.normalOffset;
    A.dPos[o] = px + this._n.x * off;
    A.dPos[o + 1] = py + this._n.y * off;
    A.dPos[o + 2] = pz + this._n.z * off;
    o = i * 4;
    A.dQuat[o] = this._q.x; A.dQuat[o + 1] = this._q.y; A.dQuat[o + 2] = this._q.z; A.dQuat[o + 3] = this._q.w;
    A.dParams[o] = size; A.dParams[o + 1] = this.time; A.dParams[o + 2] = life; A.dParams[o + 3] = fadeDur;
    A.dCol[o] = r; A.dCol[o + 1] = g; A.dCol[o + 2] = b; A.dCol[o + 3] = cellIndex;
    A.dExtra[o] = growTime; A.dExtra[o + 1] = growFrom; A.dExtra[o + 2] = gloss; A.dExtra[o + 3] = 0;

    this._touch(i);
    this.geometry.instanceCount = this._wrapped ? this.capacity : this.head;
    return i;
  }

  /** Bring a slot's lifetime forward so it fades out now rather than popping. */
  _retire(i) {
    const A = this.arrays;
    const o = i * 4;
    const birth = A.dParams[o + 1];
    const life = A.dParams[o + 2];
    if (life <= 0) return;
    const remaining = birth + life - this.time;
    if (remaining <= TUNING.recycleFade) return;
    A.dParams[o + 2] = this.time + TUNING.recycleFade - birth;
    A.dParams[o + 3] = TUNING.recycleFade;
    this._touch(i);
  }

  /** Re-target an existing decal (used to keep a blood pool under a body). */
  move(i, px, py, pz) {
    const o = i * 3;
    this.arrays.dPos[o] = px;
    this.arrays.dPos[o + 1] = py;
    this.arrays.dPos[o + 2] = pz;
    this._touch(i);
  }

  setSun(dir, color, ambient) {
    if (dir) this.uniforms.uSunDir.value.copy(dir).normalize();
    if (color) this.uniforms.uSunColor.value.copy(color);
    if (ambient) this.uniforms.uAmbient.value.copy(ambient);
  }

  update(dt, elapsed) {
    this.time = elapsed;
    this.uniforms.uTime.value = elapsed;
    this.uniforms.uCamPos.value.copy(this.ctx.camera.position);
    if (this._lo < 0) return;
    const count = this._hi - this._lo + 1;
    for (const name in STRIDES) {
      const s = STRIDES[name];
      const attr = this.attrs[name];
      attr.addUpdateRange(this._lo * s, count * s);
      attr.needsUpdate = true;
    }
    this._lo = this._hi = -1;
    void dt;
  }

  dispose() {
    this.geometry.dispose();
    this.material.dispose();
    this.textures.albedo.dispose();
    this.textures.normal.dispose();
  }
}
